import React, { useContext, useMemo, useState } from "react";
import { Box, Button, Flex, ModalCloseButton } from "@chakra-ui/react";

import ResetAllFiltersButton from "components/ResetAllFiltersButton";
import { ChevronBack } from "icons";
import { Filter } from "../filter-definition";

type FilterModalContextValues = {
  isActive: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const FilterModalContext = React.createContext<FilterModalContextValues>(
  {} as FilterModalContextValues
);

export const useFilterModalContext = () => useContext(FilterModalContext);

const FiltersModal = ({
  filters,
  isUsingInitialFilters,
}: {
  filters: Filter[];
  isUsingInitialFilters: boolean;
}) => {
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  const openFilters = useMemo(
    () => filters.filter((filter) => !openFilter || filter.key === openFilter),
    [filters, openFilter]
  );

  const isFilterPanelOpen = openFilter !== null;
  const shouldRenderResetButton = !isUsingInitialFilters && !isFilterPanelOpen;

  return (
    <>
      <Flex
        minH={12}
        py={3}
        px={6}
        alignItems="center"
        borderBottomWidth="1px"
        borderBottomColor="gray.100"
      >
        {isFilterPanelOpen && (
          <Button
            onClick={() => setOpenFilter(null)}
            variant="link"
            size="sm"
            leftIcon={<ChevronBack />}
            alignItems="center"
            color="gray.700"
          >
            Back
          </Button>
        )}
        <ModalCloseButton />
      </Flex>
      {openFilters.map(({ component: Component, key }) => (
        <FilterModalContext.Provider
          key={key}
          value={{
            isActive: key === openFilter,
            onOpen: () => setOpenFilter(key),
            onClose: () => setOpenFilter(null),
          }}
        >
          <Component variant="modal" />
        </FilterModalContext.Provider>
      ))}
      {shouldRenderResetButton ? (
        <ResetAllFiltersButton />
      ) : (
        !isFilterPanelOpen && <Box minH={10} />
      )}
    </>
  );
};

export default FiltersModal;
